import styles from "./BusinessHour.module.css";
import { useTranslation } from "react-i18next";

export interface BusinessHourInfo {
  name: string;
  active: boolean;
  abertura: string | undefined;
  fechamento: string | undefined;
}

interface BusinessHourProps {
  onChangeActive: React.ChangeEventHandler<HTMLInputElement>;
  onChangeAbertura: React.ChangeEventHandler<HTMLInputElement>;
  onChangeFechamento: React.ChangeEventHandler<HTMLInputElement>;
  onBlurAbertura?: React.FocusEventHandler<HTMLInputElement>;
  onBlurFechamento?: React.FocusEventHandler<HTMLInputElement>;
  businessHoursInfo: BusinessHourInfo;
  required?: boolean;
}

const days = {
  padrao: "standard",
  segundaFeira: "monday",
  tercaFeira: "tuesday",
  quartaFeira: "wednesday",
  quintaFeira: "thursday",
  sextaFeira: "friday",
  sabado: "saturday",
  domingo: "sunday",
} as const;

type daysType = keyof typeof days;

/** Converte texto legado (ex.: "9:5") para o formato HH:mm exigido por input[type=time]. */
function toTimeInputValue(raw: string | undefined | null): string {
  if (raw == null || raw.trim() === "") return "";
  const normalized = raw.includes(":") ? raw : `${raw}:00`;
  const [h, m] = normalized.split(":").map((s) => s.trim());
  if (h === undefined) return "";
  const hi = Math.min(23, Math.max(0, parseInt(h, 10) || 0));
  const mi = Math.min(
    59,
    Math.max(0, parseInt((m ?? "0").replace(/\D/g, "").slice(0, 2) || "0", 10))
  );
  return `${String(hi).padStart(2, "0")}:${String(mi).padStart(2, "0")}`;
}

const BusinessHour = ({
  onChangeActive,
  onChangeAbertura,
  onChangeFechamento,
  onBlurAbertura,
  onBlurFechamento,
  businessHoursInfo,
  required,
}: BusinessHourProps) => {
  const { t } = useTranslation();
  const myName = businessHoursInfo.name as daysType;
  const dayKey = days[myName];
  const labelRaw = t(`accountMenu.${dayKey}`);
  const capitalizedName =
    labelRaw.charAt(0).toUpperCase() + labelRaw.slice(1);

  const disabled = !businessHoursInfo.active;

  return (
    <div
      className={`${styles.main_container} ${
        dayKey === "standard" ? styles.standard : ""
      }`}
    >
      <div className={styles.checkbox_container}>
        <input
          type="checkbox"
          name={businessHoursInfo.name}
          id={dayKey}
          checked={businessHoursInfo.active}
          onChange={onChangeActive}
          required={required}
        />
        <label htmlFor={dayKey}>{capitalizedName}</label>
      </div>

      <div className={styles.business_hours_container}>
        <span> {t(`accountMenu.from`)} </span>
        <input
          type="time"
          step={60}
          className={styles.time_input}
          name={businessHoursInfo.name}
          value={toTimeInputValue(businessHoursInfo.abertura)}
          onChange={onChangeAbertura}
          onBlur={onBlurAbertura}
          disabled={disabled}
          required={required && businessHoursInfo.active}
        />
        <span> {t(`accountMenu.to`)} </span>
        <input
          type="time"
          step={60}
          className={styles.time_input}
          name={businessHoursInfo.name}
          value={toTimeInputValue(businessHoursInfo.fechamento)}
          onChange={onChangeFechamento}
          onBlur={onBlurFechamento}
          disabled={disabled}
          required={required && businessHoursInfo.active}
        />
      </div>
    </div>
  );
};

export default BusinessHour;
